/**
 * ConfirmProvider — global imperative confirm dialog.
 *
 * Replaces native `window.confirm()` so prompts use the in-house
 * Modal primitive (focus trap, Escape, brand styling, accessibility)
 * instead of the OS dialog.
 *
 * API:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Discard changes?',
 *     message: 'You have unsaved edits. Leave anyway?',
 *     confirmLabel: 'Leave',
 *     confirmVariant: 'danger',
 *   });
 *   if (ok) doIt();
 *
 * Returns a Promise<boolean>: resolves true on confirm, false on
 * cancel (modal close, Escape, or backdrop click).
 *
 * Pairs with `useUnsavedChangesWarning` in lib/forms.ts.
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ConfirmModal } from './ui/Modal';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  confirmVariant?: 'primary' | 'danger' | 'success';
  // When set, renders a prominent warning callout above the message —
  // used for escalated confirmations (e.g. "this also deletes N candidates").
  warning?: string;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const fn = useContext(ConfirmContext);
  if (!fn) {
    throw new Error('useConfirm must be used within <ConfirmProvider>');
  }
  return fn;
}

interface PendingPrompt {
  options: ConfirmOptions;
  resolve: (ok: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingPrompt | null>(null);
  // Track the latest pending so the provider can resolve correctly even
  // if React batched a stale closure during a fast double-trigger.
  const pendingRef = useRef<PendingPrompt | null>(null);
  pendingRef.current = pending;

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      // If something else is already prompting, the new prompt wins —
      // the previous one resolves false (treated as cancel) so callers
      // don't hang forever on stacked prompts.
      const stale = pendingRef.current;
      if (stale) stale.resolve(false);
      setPending({ options, resolve });
    });
  }, []);

  function resolve(value: boolean) {
    const cur = pendingRef.current;
    if (cur) {
      cur.resolve(value);
    }
    setPending(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmModal
        isOpen={pending !== null}
        onClose={() => resolve(false)}
        onConfirm={() => resolve(true)}
        title={pending?.options.title ?? ''}
        message={pending?.options.message ?? ''}
        warning={pending?.options.warning}
        confirmLabel={pending?.options.confirmLabel ?? 'Confirm'}
        confirmVariant={pending?.options.confirmVariant ?? 'primary'}
      />
    </ConfirmContext.Provider>
  );
}
